import React, { useMemo, useState } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useAlert } from 'src/contexts/alert-context';
import { usePropertyImportEnqueue } from 'src/modules/shared/hooks/api/catalog/properties.mutation';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { FileInput } from 'src/modules/shared/components/atoms/inputs/file-input';
import {
  FilesService,
  type CatalogPropertyFileImportBodyParams,
  TCPropertyImportFilterCondition as PropertyImportFilterCondition,
  TCPropertyImportAssignmentMode as PropertyImportAssignmentMode,
  TCPropertyImportSyncMissingPolicy as PropertyImportSyncMissingPolicy,
  TCRealEstateProviderId as RealEstateProviderId,
} from 'src/api/generated';
import { fileToBase64 } from 'src/modules/shared/utils/file/base64.util';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import {
  buildAssignmentModeOptions,
  getPropertyImportFileFormatConfig,
} from 'src/modules/catalog/utils/property-import.utils';

interface PropertyImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ImportFilterMatchType = PropertyImportFilterCondition['matchType'];

const IMPORT_FILTER_MATCH_TYPES = new Set<string>(
  Object.values(PropertyImportFilterCondition.matchType)
);
const REAL_ESTATE_PROVIDER_IDS = new Set<string>(
  Object.values(RealEstateProviderId)
);
const SYNC_MISSING_POLICIES = new Set<string>(
  Object.values(PropertyImportSyncMissingPolicy)
);

interface ImportFilterFormState {
  columnName: string;
  value: string;
  matchType: ImportFilterMatchType;
}

const isImportFilterMatchType = (
  value: string
): value is ImportFilterMatchType => IMPORT_FILTER_MATCH_TYPES.has(value);

const isRealEstateProviderId = (value: string): value is RealEstateProviderId =>
  REAL_ESTATE_PROVIDER_IDS.has(value);

const isPropertyImportSyncMissingPolicy = (
  value: string
): value is PropertyImportSyncMissingPolicy => SYNC_MISSING_POLICIES.has(value);

const defaultFilterColumnByProvider = (
  providerId: RealEstateProviderId
): string => {
  switch (providerId) {
    case RealEstateProviderId.PROP360:
      return 'Agente';
    case RealEstateProviderId.KUTT:
      return 'Comuna';
    case RealEstateProviderId.ILIST:
      return 'Comuna';
    case RealEstateProviderId.GLOBALBROKERS:
      return 'Comuna';
  }
};

const buildDefaultFilterForm = (
  providerId: RealEstateProviderId
): ImportFilterFormState => ({
  columnName: defaultFilterColumnByProvider(providerId),
  value: '',
  matchType: PropertyImportFilterCondition.matchType.EQUALS,
});

const getFilterColumnOptions = (
  providerId: RealEstateProviderId,
  t: (key: TranslationKey) => string
): { value: string; content: string }[] => {
  switch (providerId) {
    case RealEstateProviderId.PROP360:
      return [
        {
          value: 'Agente',
          content: t('catalog.properties.import.filter.column.agente'),
        },
        {
          value: 'Oficina',
          content: t('catalog.properties.import.filter.column.oficina'),
        },
        {
          value: 'Tipo 1',
          content: t('catalog.properties.import.filter.column.type1'),
        },
        {
          value: 'Estado',
          content: t('catalog.properties.import.filter.column.status'),
        },
        {
          value: 'Región',
          content: t('catalog.properties.import.filter.column.region'),
        },
      ];
    case RealEstateProviderId.KUTT:
      return [
        {
          value: 'Comuna',
          content: t('catalog.properties.import.kutt.filter.column.comuna'),
        },
        {
          value: 'Region',
          content: t('catalog.properties.import.kutt.filter.column.region'),
        },
        {
          value: 'Tipo Propiedad',
          content: t(
            'catalog.properties.import.kutt.filter.column.propertyType'
          ),
        },
        {
          value: 'Tipo Operacion',
          content: t('catalog.properties.import.kutt.filter.column.operation'),
        },
        {
          value: 'Estado',
          content: t('catalog.properties.import.kutt.filter.column.status'),
        },
      ];
    case RealEstateProviderId.ILIST:
      return [
        {
          value: 'Agente',
          content: t('catalog.properties.import.ilist.filter.column.agent'),
        },
        {
          value: 'Comuna',
          content: t('catalog.properties.import.ilist.filter.column.locality'),
        },
        {
          value: 'Provincia',
          content: t('catalog.properties.import.ilist.filter.column.province'),
        },
        {
          value: 'Tipo Propiedad',
          content: t(
            'catalog.properties.import.ilist.filter.column.propertyType'
          ),
        },
        {
          value: 'Estado de la Captacion',
          content: t('catalog.properties.import.ilist.filter.column.status'),
        },
      ];
    case RealEstateProviderId.GLOBALBROKERS:
      return [
        {
          value: 'Comuna',
          content: t(
            'catalog.properties.import.globalbrokers.filter.column.locality'
          ),
        },
        {
          value: 'Estado publicación',
          content: t(
            'catalog.properties.import.globalbrokers.filter.column.status'
          ),
        },
        {
          value: 'Tipo de propiedad',
          content: t(
            'catalog.properties.import.globalbrokers.filter.column.propertyType'
          ),
        },
        {
          value: 'Tipo de publicación',
          content: t(
            'catalog.properties.import.globalbrokers.filter.column.operation'
          ),
        },
        {
          value: 'Tipo de moneda',
          content: t(
            'catalog.properties.import.globalbrokers.filter.column.currency'
          ),
        },
      ];
  }
};

export const PropertyImportModal: React.FC<PropertyImportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const [providerId, setProviderId] = useState<RealEstateProviderId>(
    RealEstateProviderId.PROP360
  );
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFilterEnabled, setIsFilterEnabled] = useState(false);
  const [filterForm, setFilterForm] = useState<ImportFilterFormState>(() =>
    buildDefaultFilterForm(RealEstateProviderId.PROP360)
  );
  const [assignmentMode, setAssignmentMode] =
    useState<PropertyImportAssignmentMode>(PropertyImportAssignmentMode.KEEP);
  const [assignmentUserId, setAssignmentUserId] = useState<number | null>(null);
  const [syncMissingPolicy, setSyncMissingPolicy] =
    useState<PropertyImportSyncMissingPolicy>(
      PropertyImportSyncMissingPolicy.UPSERT
    );

  const { enqueuePropertyImport, loading } = usePropertyImportEnqueue({
    onSuccess: () => {
      showAlert(t('catalog.properties.import.alert.success'), 'success');
      handleClose();
    },
    onError: (error) => {
      const message = extractApiErrorMessage(error);
      showAlert(message, 'error');
    },
  });

  const handleClose = () => {
    setFile(null);
    setIsFilterEnabled(false);
    setFilterForm(buildDefaultFilterForm(providerId));
    setAssignmentMode(PropertyImportAssignmentMode.KEEP);
    setAssignmentUserId(null);
    setSyncMissingPolicy(PropertyImportSyncMissingPolicy.UPSERT);
    onClose();
  };

  const providerOptions = useMemo(
    () => [
      {
        value: RealEstateProviderId.PROP360,
        content: t('catalog.properties.import.provider.option.prop360'),
      },
      {
        value: RealEstateProviderId.KUTT,
        content: t('catalog.properties.import.provider.option.kutt'),
      },
      {
        value: RealEstateProviderId.ILIST,
        content: t('catalog.properties.import.provider.option.ilist'),
      },
      {
        value: RealEstateProviderId.GLOBALBROKERS,
        content: t('catalog.properties.import.provider.option.globalbrokers'),
      },
    ],
    [t]
  );

  const filterColumnOptions = useMemo(
    () => getFilterColumnOptions(providerId, t),
    [providerId, t]
  );
  const fileFormatConfig = useMemo(
    () => getPropertyImportFileFormatConfig(providerId),
    [providerId]
  );

  const matchTypeOptions = useMemo(
    () => [
      {
        value: PropertyImportFilterCondition.matchType.EQUALS,
        content: t('catalog.properties.import.filter.matchType.equals'),
      },
      {
        value: PropertyImportFilterCondition.matchType.CONTAINS,
        content: t('catalog.properties.import.filter.matchType.contains'),
      },
      {
        value: PropertyImportFilterCondition.matchType.STARTS_WITH,
        content: t('catalog.properties.import.filter.matchType.startsWith'),
      },
    ],
    [t]
  );

  const handleProviderChange = (nextProviderId: RealEstateProviderId) => {
    setProviderId(nextProviderId);
    setFile(null);
    setIsFilterEnabled(false);
    setFilterForm(buildDefaultFilterForm(nextProviderId));
    setAssignmentMode(PropertyImportAssignmentMode.KEEP);
    setAssignmentUserId(null);
    setSyncMissingPolicy(PropertyImportSyncMissingPolicy.UPSERT);
  };

  const usersFilterBy = useMemo(
    () => createQuery().equals('kind', 'person').build().filterBy,
    []
  );
  const { users, loading: usersLoading } = useAllUsers({
    filterBy: usersFilterBy,
  });

  const assignmentModeOptions = useMemo(() => {
    const options: {
      value: PropertyImportAssignmentMode;
      content: string;
    }[] = [
      {
        value: PropertyImportAssignmentMode.KEEP,
        content: t('catalog.properties.import.assignment.mode.keep'),
      },
      {
        value: PropertyImportAssignmentMode.FORCE_USER,
        content: t('catalog.properties.import.assignment.mode.forceUser'),
      },
    ];

    if (
      providerId === RealEstateProviderId.PROP360 ||
      providerId === RealEstateProviderId.ILIST
    ) {
      options.push({
        value: PropertyImportAssignmentMode.AUTO,
        content: t('catalog.properties.import.assignment.mode.auto'),
      });
    }

    return options;
  }, [providerId, t]);

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: String(user.id),
        keywords:
          `${user.firstName ?? ''} ${user.lastName ?? ''} ${user.email}`.trim(),
        content: formatFullName(user.firstName, user.lastName, user.email),
      })),
    [users]
  );

  const assignmentHelpKey = useMemo<TranslationKey>(() => {
    switch (assignmentMode) {
      case PropertyImportAssignmentMode.KEEP:
        return 'catalog.properties.import.assignment.help.keep';
      case PropertyImportAssignmentMode.FORCE_USER:
        return 'catalog.properties.import.assignment.help.forceUser';
      case PropertyImportAssignmentMode.AUTO:
        return 'catalog.properties.import.assignment.help.auto';
      default:
        return 'catalog.properties.import.assignment.help.keep';
    }
  }, [assignmentMode]);

  const syncMissingPolicyOptions = useMemo(() => {
    const options: {
      value: PropertyImportSyncMissingPolicy;
      content: string;
    }[] = [
      {
        value: PropertyImportSyncMissingPolicy.UPSERT,
        content: t('catalog.properties.import.syncMissingPolicy.option.upsert'),
      },
    ];
    if (providerId !== RealEstateProviderId.GLOBALBROKERS) {
      options.push({
        value: PropertyImportSyncMissingPolicy.REPLACE,
        content: t(
          'catalog.properties.import.syncMissingPolicy.option.replace'
        ),
      });
    }
    return options;
  }, [providerId, t]);

  const handleConfirm = async () => {
    if (!file) {
      showAlert(t(fileFormatConfig.noFileAlertKey), 'error');
      return;
    }

    if (isFilterEnabled && !filterForm.value.trim()) {
      showAlert(
        t('catalog.properties.import.alert.filterValueRequired'),
        'error'
      );
      return;
    }

    const mustForceUserMode =
      assignmentMode === PropertyImportAssignmentMode.FORCE_USER &&
      !assignmentUserId;
    if (mustForceUserMode) {
      showAlert(
        t('catalog.properties.import.assignment.alert.userRequired'),
        'error'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const content = await fileToBase64(file);
      const uploadResponse = await FilesService.uploadFile({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        content,
        tags: {
          source: providerId,
          kind: 'property-import',
        },
      });

      const trimmedFilterValue = filterForm.value.trim();
      const filterCondition =
        isFilterEnabled && trimmedFilterValue
          ? {
              columnName: filterForm.columnName,
              value: trimmedFilterValue,
              matchType: filterForm.matchType,
            }
          : null;

      const options = buildAssignmentModeOptions({
        assignmentMode,
        assignmentUserId,
        filterCondition,
        syncMissingPolicy:
          providerId === RealEstateProviderId.GLOBALBROKERS
            ? PropertyImportSyncMissingPolicy.UPSERT
            : syncMissingPolicy,
      });

      if (!uploadResponse.id) {
        throw new Error('Error al subir el archivo');
      }

      const requestBody: CatalogPropertyFileImportBodyParams = {
        providerId,
        fileId: uploadResponse.id,
        originalFilename: file.name,
        options,
      };

      await enqueuePropertyImport(requestBody);
    } catch (error) {
      const message = extractApiErrorMessage(error);
      showAlert(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      dialogClassName="sm:max-w-lg"
      header={
        <Text size="h5" weight="semibold">
          {t('catalog.properties.import.title')}
        </Text>
      }
      body={
        <div className="flex flex-col gap-6 w-full">
          <Text size="small" color="secondary">
            {t(fileFormatConfig.descriptionKey)}
          </Text>

          <div className="flex flex-row gap-2">
            <Label size="sm" className="w-1/3">
              {t('catalog.properties.import.provider.label')}
            </Label>
            <div className="w-2/3">
              <Select
                className="w-full"
                options={providerOptions}
                size="sm"
                value={providerId}
                onChange={(event) => {
                  const next = event.target.value;
                  if (!isRealEstateProviderId(next)) {
                    return;
                  }
                  handleProviderChange(next);
                }}
              />
            </div>
          </div>

          <div className="flex flex-row gap-2">
            <Label size="sm" className="w-1/3">
              {t(fileFormatConfig.labelKey)}
            </Label>
            <FileInput
              key={providerId}
              onChange={(selectedFile) => setFile(selectedFile)}
              containerClassName="flex justify-start w-2/3"
              accept={fileFormatConfig.accept}
            />
          </div>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex items-center justify-end">
              <LabelSwitch
                label={t('catalog.properties.import.filter.toggle')}
                checked={isFilterEnabled}
                onChange={setIsFilterEnabled}
                size="sm"
              />
            </div>

            {isFilterEnabled && (
              <div className="flex flex-col gap-3 sm:flex-row items-end md:items-start">
                <div className="flex flex-1 w-full">
                  <LabelSelect
                    label={t('catalog.properties.import.filter.columnLabel')}
                    options={filterColumnOptions}
                    size="sm"
                    labelPosition="top"
                    className="w-full"
                    selectProps={{
                      value: filterForm.columnName,
                      onChange: (event) =>
                        setFilterForm((current) => ({
                          ...current,
                          columnName: event.target.value,
                        })),
                    }}
                  />
                </div>
                <div className="flex flex-1 w-full">
                  <Select
                    className="w-full"
                    options={matchTypeOptions}
                    size="sm"
                    value={filterForm.matchType}
                    onChange={(event) =>
                      setFilterForm((current) => {
                        const nextMatchType = event.target.value;
                        if (!isImportFilterMatchType(nextMatchType)) {
                          return current;
                        }
                        return {
                          ...current,
                          matchType: nextMatchType,
                        };
                      })
                    }
                  />
                </div>
                <div className="flex flex-1 w-full">
                  <TextInput
                    className="w-full"
                    size="sm"
                    value={filterForm.value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setFilterForm((current) => ({
                        ...current,
                        value: event.target.value,
                      }))
                    }
                    placeholder={t(
                      'catalog.properties.import.filter.valueLabel'
                    )}
                    error={
                      !filterForm.value.trim()
                        ? t(
                            'catalog.properties.import.alert.filterValueRequired'
                          )
                        : undefined
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-row gap-2 items-start">
              <Label size="sm" className="w-1/3">
                {t('catalog.properties.import.syncMissingPolicy.label')}
              </Label>
              <div className="flex flex-col gap-2 w-2/3">
                <Select
                  className="w-full"
                  options={syncMissingPolicyOptions}
                  size="sm"
                  value={syncMissingPolicy}
                  onChange={(event) => {
                    const next = event.target.value;
                    if (!isPropertyImportSyncMissingPolicy(next)) {
                      return;
                    }
                    setSyncMissingPolicy(next);
                  }}
                />
                <Text size="small" color="secondary">
                  {t('catalog.properties.import.syncMissingPolicy.help')}
                </Text>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-row gap-2 items-start">
              <Label size="sm" className="w-1/3">
                {t('catalog.properties.import.assignment.label')}
              </Label>
              <div className="flex flex-col gap-2 w-2/3">
                <Select
                  className="w-full"
                  options={assignmentModeOptions}
                  size="sm"
                  value={assignmentMode}
                  onChange={(event) => {
                    const next = event.target
                      .value as PropertyImportAssignmentMode;
                    setAssignmentMode(next);
                    if (next !== PropertyImportAssignmentMode.FORCE_USER) {
                      setAssignmentUserId(null);
                    }
                  }}
                />

                {assignmentMode === PropertyImportAssignmentMode.FORCE_USER && (
                  <SearchableSelect
                    options={userOptions}
                    value={
                      assignmentUserId ? String(assignmentUserId) : undefined
                    }
                    onChange={(value) => {
                      const next = Number(value);
                      setAssignmentUserId(Number.isFinite(next) ? next : null);
                    }}
                    placeholder={t(
                      'catalog.properties.import.assignment.userPlaceholder'
                    )}
                    emptyLabel={t(
                      'catalog.properties.import.assignment.userEmpty'
                    )}
                    size="sm"
                    loading={usersLoading}
                    disabled={usersLoading || userOptions.length === 0}
                  />
                )}

                <Text size="small" color="secondary">
                  {t(assignmentHelpKey)}
                </Text>
              </div>
            </div>
          </div>
        </div>
      }
      footer={
        <ModalFooter
          onCancel={handleClose}
          onConfirm={handleConfirm}
          confirmText={t('catalog.properties.import.confirm')}
          cancelText={t('catalog.products.form.cancel')}
          loading={loading || isSubmitting}
          confirmDisabled={loading || isSubmitting}
        />
      }
    />
  );
};
